import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Shared markdown renderer (SCRUM-2203 — MVP 1.40.3).
 *
 * Used by both StaticPage (help/safety/cancellation) and LegalDocumentPage
 * (/privacy, /terms, /eula, /guide-agreement) so visual fidelity is identical.
 *
 * Renders gfm markdown with the project's typography classes. Raw HTML in the
 * source is rendered inert — we do NOT include rehype-raw.
 */

function generateHeadingId(children) {
  if (typeof children === 'string') {
    return children.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
  }
  if (Array.isArray(children)) {
    return children
      .map(child => (typeof child === 'string' ? child : ''))
      .join('')
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  }
  return '';
}

const COMPONENTS = {
  h1: ({ node: _node, children, ...props }) => (
    <h1 id={generateHeadingId(children)} className="text-3xl font-bold text-gray-900 mt-8 mb-4 border-b pb-2 scroll-mt-24" {...props}>
      {children}
    </h1>
  ),
  h2: ({ node: _node, children, ...props }) => (
    <h2 id={generateHeadingId(children)} className="text-2xl font-semibold text-gray-800 mt-6 mb-3 scroll-mt-24" {...props}>
      {children}
    </h2>
  ),
  h3: ({ node: _node, children, ...props }) => (
    <h3 id={generateHeadingId(children)} className="text-xl font-semibold text-gray-800 mt-4 mb-2 scroll-mt-24" {...props}>
      {children}
    </h3>
  ),
  h4: ({ node: _node, children, ...props }) => (
    <h4 id={generateHeadingId(children)} className="text-lg font-semibold text-gray-800 mt-3 mb-2 scroll-mt-24" {...props}>
      {children}
    </h4>
  ),
  h5: ({ node: _node, children, ...props }) => (
    <h5 id={generateHeadingId(children)} className="text-base font-semibold text-gray-800 mt-2 mb-1 scroll-mt-24" {...props}>
      {children}
    </h5>
  ),
  h6: ({ node: _node, children, ...props }) => (
    <h6 id={generateHeadingId(children)} className="text-sm font-semibold text-gray-800 mt-2 mb-1 scroll-mt-24" {...props}>
      {children}
    </h6>
  ),
  p: ({ node: _node, ...props }) => (
    <p className="text-gray-700 leading-relaxed mb-4" {...props} />
  ),
  ul: ({ node: _node, ...props }) => (
    <ul className="list-disc list-inside space-y-2 mb-4 ml-4" {...props} />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol className="list-decimal list-inside space-y-2 mb-4 ml-4" {...props} />
  ),
  li: ({ node: _node, ...props }) => (
    <li className="text-gray-700" {...props} />
  ),
  a: ({ node: _node, ...props }) => (
    <a className="text-brand-primary hover:text-brand-dark underline" {...props} />
  ),
  blockquote: ({ node: _node, ...props }) => (
    <blockquote className="border-l-4 border-brand-primary bg-gray-50 pl-4 py-2 my-4 italic" {...props} />
  ),
  code: ({ node: _node, inline, ...props }) => (
    inline
      ? <code className="bg-gray-100 text-brand-dark px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
      : <code className="block bg-gray-100 p-4 rounded text-sm font-mono overflow-x-auto" {...props} />
  ),
  pre: ({ node: _node, ...props }) => (
    <pre className="bg-gray-100 p-4 rounded overflow-x-auto mb-4" {...props} />
  ),
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto my-6">
      <table className="min-w-full divide-y divide-gray-200 border" {...props} />
    </div>
  ),
  thead: ({ node: _node, ...props }) => (
    <thead className="bg-gray-50" {...props} />
  ),
  tbody: ({ node: _node, ...props }) => (
    <tbody className="bg-white divide-y divide-gray-200" {...props} />
  ),
  th: ({ node: _node, ...props }) => (
    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border" {...props} />
  ),
  td: ({ node: _node, ...props }) => (
    <td className="px-4 py-3 text-sm text-gray-700 border" {...props} />
  ),
  tr: ({ node: _node, ...props }) => (
    <tr {...props} />
  ),
  hr: ({ node: _node, ...props }) => (
    <hr className="my-8 border-t border-gray-300" {...props} />
  ),
  strong: ({ node: _node, ...props }) => (
    <strong className="font-semibold text-gray-900" {...props} />
  ),
  em: ({ node: _node, ...props }) => (
    <em className="italic" {...props} />
  ),
};

export default function MarkdownContent({ children }) {
  return (
    <div className="prose prose-lg max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {children || ''}
      </ReactMarkdown>
    </div>
  );
}
